import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Layers,
  MapPin,
  Plug,
  Rocket,
  Store,
  UserPlus,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PartnerPicker } from "@/components/PartnerPicker";
import { SchemaForm } from "@/components/SchemaForm";
import { Toggle } from "@/components/Toggle";
import { usePartnerScope } from "@/components/partner-scope-context";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { fieldScope } from "@/lib/binding-scope";
import { isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { buildValuesFromSchema, hasProperties } from "@/lib/schema-form";
import { browserTimezone, timezoneChoicesWithBrowser } from "@/lib/timezones";
import { cn } from "@/lib/utils";
import { validateEmail, validatePhone } from "@/lib/validation";
import type {
  Application,
  BindingScope,
  FeatureCatalogEntry,
  IntegrationCategory,
  ProviderBindingCreateRequest,
  ProviderCatalogEntry,
  ProviderEnvironment,
  UserRole,
  Vertical,
} from "@tilt/admin-sdk";

/**
 * One-shot new-partner onboarding wizard.
 *
 * Steps: Partner → Organization → Location → Providers → Admin → Review.
 *
 * The goal is a partner admin can stand up a brand-new partner +
 * organization + first location + fully-wired provider bindings +
 * first admin user in under three minutes. Each step has sensible
 * defaults so the "happy path" is fill a name, tab, fill another
 * name, Next, fill the Valor creds, Next, invite yourself, Create
 * everything.
 *
 * All API calls on submit run sequentially because each uses IDs
 * returned by earlier calls. No rollback on partial failure - the
 * error surfaces which step broke so the admin can finish manually.
 */

type StepId =
  | "kind"
  | "partner"
  | "capabilities"
  | "organization"
  | "location"
  | "providers"
  | "admin"
  | "review";

/** Quick-onboard kind (#163). "new_partner" creates a partner +
 *  org + location chain; "existing_partner" picks an existing
 *  partner and only creates the org + location underneath. The
 *  Partner step is skipped in the latter mode. */
type OnboardKind = "new_partner" | "existing_partner";

interface StepDef {
  id: StepId;
  label: string;
  icon: typeof Building2;
}

/** All possible steps. The actual wizard flow is built from this list
 *  by ``stepsForKind`` - "existing_partner" mode drops the "partner"
 *  step entirely (the picker on the kind step provides the parent
 *  partner directly). */
const ALL_STEPS: StepDef[] = [
  { id: "kind", label: "What to onboard", icon: Rocket },
  { id: "partner", label: "Partner", icon: Building2 },
  { id: "capabilities", label: "Apps & features", icon: Plug },
  { id: "organization", label: "Organization", icon: Store },
  { id: "location", label: "Location", icon: MapPin },
  { id: "providers", label: "Providers", icon: Plug },
  { id: "admin", label: "Admin user", icon: UserPlus },
  { id: "review", label: "Review", icon: Rocket },
];

function stepsForKind(kind: OnboardKind): StepDef[] {
  // Capabilities step only shows in new_partner mode - apps + features
  // for an existing partner are already configured and inherit down to
  // the new org/location.
  if (kind === "new_partner") return ALL_STEPS;
  return ALL_STEPS.filter((s) => s.id !== "partner" && s.id !== "capabilities");
}

interface PartnerDraft {
  parent_id: string;
  name: string;
  slug: string;
  slug_touched: boolean;
  can_manage_sub_partners: boolean;
  /** V0036 - Platform-only. Lets the new partner's admins grant
   *  ``can_manage_sub_partners=true`` on their sub-partners. */
  can_cascade_sub_partner_flag: boolean;
  /** V0036 - Platform-only. Lets the new partner's admins start
   *  impersonation sessions on direct-child users. */
  can_impersonate: boolean;
}

interface OrganizationDraft {
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  address: string;
}

interface LocationDraft {
  name: string;
  slug: string;
  slug_touched: boolean;
  vertical_id: string;
  seed_departments: boolean;
  timezone: string;
  sales_tax_rate: string;
}

interface ProviderBindingDraft {
  id: string;
  provider_id: string;
  /** Routing key for which feature this binding serves (e.g.
   *  ``pos.payments.card_present`` or ``pos.inventory.search``). */
  feature_path: string;
  scope: BindingScope;
  environment: ProviderEnvironment;
  credential_label: string;
  credential_values: Record<string, unknown>;
  skip_credential: boolean;
  config_values: Record<string, unknown>;
}

interface AdminDraft {
  invite: boolean;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
}

const CATEGORY_ORDER: IntegrationCategory[] = [
  "payment",
  "dms",
  "inventory",
  "communication",
  "analytics",
  "ai",
];
const CATEGORY_LABEL: Record<IntegrationCategory, string> = {
  payment: "Payments",
  dms: "Dealer management",
  inventory: "Inventory",
  communication: "Communication",
  analytics: "Analytics",
  ai: "AI",
};

/**
 * Default ``Provider [- Subtype] - environment`` label for a binding's
 * credential. ``Valor - Pay by Text - sandbox`` instead of the bare
 * ``Valor sandbox`` so an admin staring at three Valor credential
 * rows on the Credentials page can tell them apart at a glance. Same
 * format gets used as the placeholder and as the submit-time
 * fallback when the admin leaves the label field blank.
 */
function defaultCredentialLabel(
  provider: ProviderCatalogEntry | undefined,
  featurePath: string | undefined,
  environment: ProviderEnvironment,
): string {
  const providerName = provider?.display_name ?? "Credential";
  const parts: string[] = [providerName];
  if (featurePath) {
    const labels = featurePath.split(".");
    const leaf = labels[labels.length - 1] ?? featurePath;
    parts.push(leaf.replace(/_/g, " "));
  }
  parts.push(environment);
  return parts.join(" - ");
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30);
}

/** Bucket providers by category, preserving CATEGORY_ORDER. Missing
 *  ``category`` (pre-V0030 catalog rows) defaults to ``payment``. */
function groupProvidersByCategory(
  providers: ProviderCatalogEntry[],
): Array<{ category: IntegrationCategory; providers: ProviderCatalogEntry[] }> {
  const buckets = new Map<IntegrationCategory, ProviderCatalogEntry[]>();
  for (const p of providers) {
    const cat: IntegrationCategory = p.category ?? "payment";
    const bucket = buckets.get(cat) ?? [];
    bucket.push(p);
    buckets.set(cat, bucket);
  }
  return CATEGORY_ORDER.filter((c) => buckets.has(c)).map((c) => ({
    category: c,
    // Non-null because filter() already checked has(c).
    providers: buckets.get(c)!,
  }));
}

export function OnboardPage(): JSX.Element {
  usePageTitle("Onboard");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { scope } = usePartnerScope();
  const profile = useRoleProfile();
  // Only Platform admins (super_admin) can grant the V0036 cascade /
  // impersonate flags on a new partner. Non-super onboards (a partner
  // admin spinning up a sub-partner) never see those checkboxes -
  // they can't set them on the create payload, the backend would 403,
  // and rendering them would imply a power they don't have.
  const isPlatformAdmin = isSuperAdmin(profile);

  const [kind, setKind] = useState<OnboardKind>("new_partner");
  const [step, setStep] = useState<StepId>("kind");
  // Only used when kind === "existing_partner" - the picked partner
  // becomes the org's parent. Stored separately from PartnerDraft so
  // a back-step from the kind page doesn't lose draft data.
  const [existingPartnerId, setExistingPartnerId] = useState<string>("");

  const [partner, setPartner] = useState<PartnerDraft>({
    parent_id: scope.id,
    name: "",
    slug: "",
    slug_touched: false,
    can_manage_sub_partners: false,
    can_cascade_sub_partner_flag: false,
    can_impersonate: false,
  });
  const [org, setOrg] = useState<OrganizationDraft>({
    name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
  });
  const [loc, setLoc] = useState<LocationDraft>({
    name: "",
    slug: "",
    slug_touched: false,
    vertical_id: "",
    seed_departments: true,
    // Default to the creator's browser timezone - the admin spinning
    // up a location is almost always in the same TZ as the location
    // (or close enough that they'd rather override than retype). UTC
    // fallback when the runtime doesn't surface a value.
    timezone: browserTimezone(),
    sales_tax_rate: "",
  });
  const [bindings, setBindings] = useState<ProviderBindingDraft[]>([]);
  // V0046 + V0060 - capabilities step. Defaults: every app + feature
  // ON. Sets carry the *unchecked* slugs / paths; an empty set means
  // "everything stays at the catalogue default" (no override rows
  // written). Skipped entirely in existing-partner mode.
  const [disabledAppSlugs, setDisabledAppSlugs] = useState<Set<string>>(() => new Set());
  const [disabledFeaturePaths, setDisabledFeaturePaths] = useState<Set<string>>(() => new Set());
  const [admin, setAdmin] = useState<AdminDraft>({
    invite: true,
    email: "",
    first_name: "",
    last_name: "",
    role: "partner_admin",
  });
  const [error, setError] = useState<string | null>(null);

  const verticalsQuery = useQuery({
    queryKey: ["verticals"],
    queryFn: () => apiClient.listVerticals(),
    staleTime: 60_000,
  });
  const verticals: Vertical[] = useMemo(
    () => verticalsQuery.data?.verticals ?? [],
    [verticalsQuery.data],
  );
  useEffect(() => {
    if (!loc.vertical_id && verticals.length > 0) {
      // Service is the dominant fleet/dealership vertical for this
      // platform - auto-seeds a sensible department set out of the
      // box. Falls through to "other" or the first available row if
      // service isn't catalogued in this env (shouldn't happen post-
      // V0007 seed, but keep the chain safe).
      const fallback =
        verticals.find((v) => v.slug === "service")?.id ??
        verticals.find((v) => v.slug === "other")?.id ??
        verticals[0]?.id ??
        "";
      setLoc((prev) => ({ ...prev, vertical_id: fallback }));
    }
  }, [verticals, loc.vertical_id]);

  const providersQuery = useQuery({
    queryKey: ["provider-catalog", "onboarding"],
    queryFn: () => apiClient.listProviderCatalog(),
    staleTime: 60_000,
  });
  const providerCatalog: ProviderCatalogEntry[] = useMemo(
    () => (providersQuery.data?.providers ?? []).filter((p) => p.is_enabled),
    [providersQuery.data],
  );

  // Application catalogue + feature ltree catalogue power the
  // capabilities step. Both are platform-level reads, cached briefly.
  // Only fetched when the kind step lands on new_partner - the step
  // is skipped otherwise.
  const enableCapabilityFetch = kind === "new_partner";
  const applicationsQuery = useQuery({
    queryKey: ["applications", "onboarding"],
    queryFn: () => apiClient.listPlatformApplications(),
    staleTime: 60_000,
    enabled: enableCapabilityFetch,
  });
  const applications: Application[] = useMemo(
    () => (applicationsQuery.data?.applications ?? []).filter((a) => a.is_active),
    [applicationsQuery.data],
  );

  const featureCatalogQuery = useQuery({
    queryKey: ["feature-catalog", "onboarding"],
    queryFn: () => apiClient.listFeatureCatalog(),
    staleTime: 60_000,
    enabled: enableCapabilityFetch,
  });
  const featureCatalog: FeatureCatalogEntry[] = useMemo(
    () => (featureCatalogQuery.data?.features ?? []).filter((f) => f.is_active),
    [featureCatalogQuery.data],
  );

  // Step list is kind-dependent - "existing_partner" mode skips
  // the partner-create step.
  const STEPS = useMemo(() => stepsForKind(kind), [kind]);
  const stepIndex = STEPS.findIndex((s) => s.id === step);

  // If kind flips while we're past the kind step, reset to kind so a
  // toggle doesn't strand the wizard on a step that no longer exists
  // in the current flow.
  useEffect(() => {
    if (step !== "kind" && stepIndex < 0) {
      setStep("kind");
    }
  }, [step, stepIndex]);

  const canAdvanceFrom = (id: StepId): boolean => {
    switch (id) {
      case "kind":
        // New-partner mode advances unconditionally; existing-partner
        // requires a partner pick before we know the org's parent.
        return kind === "new_partner" || !!existingPartnerId;
      case "partner":
        return !!(partner.parent_id && partner.name.trim() && partner.slug.trim());
      case "capabilities":
        // No required fields - everything defaults ON. Admin can
        // advance straight through if they want all apps + features.
        return true;
      case "organization":
        return (
          org.name.trim().length > 0 &&
          validateEmail(org.contact_email).ok &&
          validatePhone(org.contact_phone).ok
        );
      case "location":
        return !!(loc.name.trim() && loc.slug.trim() && loc.vertical_id);
      case "providers":
        return true;
      case "admin":
        if (!admin.invite) return true;
        return validateEmail(admin.email, { required: true }).ok;
      case "review":
        return true;
    }
  };

  const goNext = (): void => {
    setError(null);
    const nextIdx = Math.min(stepIndex + 1, STEPS.length - 1);
    setStep(STEPS[nextIdx].id);
  };
  const goBack = (): void => {
    setError(null);
    const prevIdx = Math.max(stepIndex - 1, 0);
    setStep(STEPS[prevIdx].id);
  };

  const submit = useMutation({
    mutationFn: async () => {
      // All-or-nothing semantics. In ``new_partner`` mode the partner
      // row is the rollback root for the whole subtree
      // (cascade-hard-delete cleans orgs, locations, departments,
      // provider creds/bindings, user memberships AND frees the
      // globally-unique partner slug for retry).
      //
      // In ``existing_partner`` mode (#163) the partner already exists
      // and must NOT be cascade-deleted on failure - the rollback root
      // becomes the org instead. We track which one with the
      // ``rollbackTarget`` discriminator below.
      let rollbackTarget: { kind: "partner" | "organization"; id: string } | null = null;

      try {
        let partnerId: string;
        if (kind === "existing_partner") {
          // Validated by canAdvanceFrom("kind"), but belt-and-suspenders.
          if (!existingPartnerId) {
            throw new Error("missing existing partner id");
          }
          partnerId = existingPartnerId;
          // Skip the patchPartner default_vertical step - the
          // existing partner already has its conventions; overwriting
          // an established default just to match the new location's
          // vertical would be invasive. createOrganization will pull
          // the existing default; if there's a mismatch the org's
          // verticals tab can sort it out post-onboard.
        } else {
          const p = await apiClient.createPartner({
            parent_id: partner.parent_id,
            partner_type: "partner",
            name: partner.name.trim(),
            slug: partner.slug.trim(),
            can_manage_sub_partners: partner.can_manage_sub_partners,
            // V0036 flags - only set when the caller is super (the
            // PartnerStep gates the inputs the same way). Backend 403s
            // for non-super, so omit entirely from non-super payloads.
            ...(isPlatformAdmin
              ? {
                  can_cascade_sub_partner_flag: partner.can_cascade_sub_partner_flag,
                  can_impersonate: partner.can_impersonate,
                }
              : {}),
          });
          partnerId = p.id;
          rollbackTarget = { kind: "partner", id: p.id };

          // V0022 default-vertical inheritance: stamp the partner's
          // default_vertical_id from the location step's vertical pick
          // BEFORE creating the org. The org-create handler reads the
          // parent partner's default and seeds
          // organization_vertical_tbl with is_default=true; without
          // this PATCH, a fresh partner has no default and the org
          // lands with zero verticals - admin would have to hop to
          // the Verticals tab before any location form would work.
          if (loc.vertical_id) {
            await apiClient.patchPartner(p.id, { default_vertical_id: loc.vertical_id });
          }

          // V0046 + V0060 - write OFF override rows for any apps /
          // features the admin unchecked on the capabilities step.
          // Defaults are everything-on, so we only write rows for the
          // disabled set. Failures here don't roll back the partner -
          // the admin can finish from the Applications tab if a write
          // fails partway through.
          for (const slug of disabledAppSlugs) {
            await apiClient.putPartnerEntitlement(p.id, slug, { is_enabled: false });
          }
          for (const featurePath of disabledFeaturePaths) {
            // Skip features whose root app is also disabled - the app
            // override already cuts that whole subtree off, no need to
            // double-write.
            const rootApp = featurePath.split(".")[0];
            if (disabledAppSlugs.has(rootApp)) continue;
            await apiClient.setFeatureEntitlement("partner", p.id, featurePath, false);
          }
        }

        const o = await apiClient.createOrganization({
          partner_id: partnerId,
          name: org.name.trim(),
          ...(org.contact_name.trim() ? { contact_name: org.contact_name.trim() } : {}),
          ...(org.contact_email.trim() ? { contact_email: org.contact_email.trim() } : {}),
          ...(org.contact_phone.trim() ? { contact_phone: org.contact_phone.trim() } : {}),
          ...(org.address.trim() ? { address: org.address.trim() } : {}),
        });
        // In existing-partner mode, the org becomes the rollback
        // root - we never touch the partner row.
        if (rollbackTarget === null) {
          rollbackTarget = { kind: "organization", id: o.id };
        }

        const l = await apiClient.createLocation({
          organization_id: o.id,
          name: loc.name.trim(),
          slug: loc.slug.trim(),
          vertical_id: loc.vertical_id,
          seed_departments_from_vertical: loc.seed_departments,
          ...(loc.timezone.trim() ? { timezone: loc.timezone.trim() } : {}),
          ...(loc.sales_tax_rate.trim()
            ? { sales_tax_rate: (Number(loc.sales_tax_rate) / 100).toFixed(4) }
            : {}),
        });

        // Credential dedupe: provider_credential_tbl is UNIQUE on
        // ``(partner, organization, location, provider, environment)``.
        // Three Valor sandbox bindings at partner scope would otherwise
        // 409 on the second create. Cache by the same composite key
        // and reuse across bindings - every Valor subtype shares one
        // credential.
        const credentialIdByKey = new Map<string, string>();
        const credKey = (b: ProviderBindingDraft, prov: ProviderCatalogEntry): string => {
          // Match the server's uniqueness shape: partner OR
          // (partner+org) OR (partner+org+loc) by scope.
          const scopePart =
            b.scope === "partner"
              ? "partner"
              : b.scope === "organization"
                ? `org:${o.id}`
                : `loc:${l.id}`;
          return `${prov.slug}|${b.environment}|${scopePart}`;
        };

        for (const b of bindings) {
          const prov = providerCatalog.find((pp) => pp.id === b.provider_id);
          if (!prov) continue;
          // V0060 - feature_path is required for the binding insert.
          // Non-payment providers (or providers not yet wired into
          // provider_feature_support_tbl) have no feature_paths and
          // can't be bound here; the binding row is skipped but the
          // credential still gets created so that work isn't lost.
          const canBind = !!b.feature_path;

          // 1) Optional credential - shared across bindings with the
          //    same (provider, environment, scope) key per the dedupe
          //    map above.
          let credentialId: string | undefined;
          if (!b.skip_credential && hasProperties(prov.credential_schema)) {
            const key = credKey(b, prov);
            const cached = credentialIdByKey.get(key);
            if (cached) {
              credentialId = cached;
            } else {
              const credResult = buildValuesFromSchema(prov.credential_schema, b.credential_values);
              if (credResult.error) {
                throw new Error(`${prov.display_name} credential: ${credResult.error}`);
              }
              // A location credential requires organization_id AND
              // location_id; an organization credential carries just
              // organization_id; partner-scope credentials carry
              // neither (matches the server validation).
              const credScopeFields: {
                organization_id?: string;
                location_id?: string;
              } = {};
              if (b.scope !== "partner") credScopeFields.organization_id = o.id;
              if (b.scope === "location") credScopeFields.location_id = l.id;
              const cred = await apiClient.createProviderCredential({
                partner_id: partnerId,
                ...credScopeFields,
                provider: prov.slug,
                environment: b.environment,
                label:
                  b.credential_label.trim() ||
                  defaultCredentialLabel(prov, b.feature_path, b.environment),
                credential: credResult.payload,
                ...(Object.keys(credResult.masked).length > 0
                  ? { masked_summary: credResult.masked }
                  : {}),
                reason: "Quick onboard - initial credential",
              });
              credentialId = cred.id;
              credentialIdByKey.set(key, cred.id);
            }
          }

          // 2) Binding config - validate against the binding's chosen
          //    scope so a partner-scope row doesn't fail required-check
          //    on a location-scope field (e.g. Valor merchant_id, which
          //    is x-scope=location and required-at-leaf - the location
          //    row carries it, not the partner row).
          const configPayload: Record<string, unknown> = {};
          if (hasProperties(prov.config_schema)) {
            const cfgResult = buildValuesFromSchema(prov.config_schema, b.config_values, {
              scope: b.scope,
            });
            if (cfgResult.error) {
              throw new Error(`${prov.display_name} config: ${cfgResult.error}`);
            }
            const props = prov.config_schema.properties ?? {};
            for (const [key, value] of Object.entries(cfgResult.payload)) {
              if (fieldScope(props[key]) === b.scope) {
                configPayload[key] = value;
              }
            }
          }

          if (!canBind) continue;

          const scopeField: Partial<ProviderBindingCreateRequest> = (() => {
            if (b.scope === "partner") return { partner_id: partnerId };
            if (b.scope === "organization") return { organization_id: o.id };
            return { location_id: l.id };
          })();
          await apiClient.createProviderBinding({
            ...scopeField,
            provider_slug: prov.slug,
            feature_path: b.feature_path,
            ...(credentialId ? { credential_id: credentialId } : {}),
            config: configPayload,
          } as ProviderBindingCreateRequest);
        }

        // 3) Optional admin invite - skipped when `admin.invite` is
        //    off. Uses inviteUser's `initial_role` block so the new
        //    user lands already bound to the new partner with the
        //    chosen role; no follow-up membership call needed.
        if (admin.invite && admin.email.trim()) {
          await apiClient.inviteUser({
            email: admin.email.trim(),
            primary_partner_id: partnerId,
            ...(admin.first_name.trim() ? { first_name: admin.first_name.trim() } : {}),
            ...(admin.last_name.trim() ? { last_name: admin.last_name.trim() } : {}),
            initial_role: { partner_id: partnerId, role: admin.role },
          });
        }

        return { partnerId, organization: o, location: l };
      } catch (err) {
        // Rollback strategy depends on mode:
        //   * new_partner: cascade-hard the partner row → cleans the
        //     whole subtree + frees the unique slug for retry.
        //   * existing_partner: no equivalent cascade endpoint at org
        //     tier. Leave the partial org/location intact and tell
        //     the admin where to inspect - they can finish the rest
        //     manually (provider bindings + admin invite are the
        //     only steps after location create) or delete the
        //     half-built org via the Org detail page.
        const detail = err instanceof Error ? err.message : String(err);
        if (rollbackTarget?.kind === "partner") {
          try {
            await apiClient.cascadeHardDeletePartner(rollbackTarget.id);
          } catch (cleanupErr) {
            console.warn("Quick onboard rollback failed:", cleanupErr);
          }
          throw new Error(
            `${detail} - partial state rolled back. You can retry with the same name.`,
          );
        }
        if (rollbackTarget?.kind === "organization") {
          throw new Error(
            `${detail} - Organization "${org.name.trim()}" was created but the rest of ` +
              "the wizard failed. Inspect or delete it from the Organization detail page; " +
              "you can re-run Quick onboard for a fresh attempt.",
          );
        }
        throw new Error(detail);
      }
    },
    onSuccess: (result) => {
      // Broad invalidation - Quick onboard touches nearly every
      // list the portal renders, so a surgical set would miss at
      // least one and leave the UI out of date after a redirect.
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
      void queryClient.invalidateQueries({ queryKey: ["organizations"] });
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
      void queryClient.invalidateQueries({ queryKey: ["provider-bindings"] });
      void queryClient.invalidateQueries({ queryKey: ["provider-bindings-resolved"] });
      void queryClient.invalidateQueries({ queryKey: ["provider-credentials"] });
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      navigate(`/partners/${result.partnerId}`);
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <div className="space-y-6 pb-16">
      <div>
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
          Quick Onboard - New Partner
        </h1>
        <p className="text-sm text-muted-foreground">
          Stand up a new partner, first organization, first location, provider bindings with
          credentials, and a first admin user - all in one flow.
        </p>
      </div>

      <ol className="flex flex-wrap items-center gap-2 text-xs">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const isActive = s.id === step;
          const isDone = idx < stepIndex;
          return (
            <li
              key={s.id}
              className={cn(
                "flex items-center gap-2 rounded-full border px-3 py-1",
                isActive
                  ? "border-primary/40 bg-primary/10 text-primary"
                  : isDone
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200"
                    : "border-muted bg-muted/30 text-muted-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-medium">
                {idx + 1}. {s.label}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="rounded-xl border bg-card p-5 shadow-sm">
        {step === "kind" && (
          <KindStep
            kind={kind}
            onKind={(k) => {
              setKind(k);
              if (k === "new_partner") {
                setExistingPartnerId("");
              }
            }}
            existingPartnerId={existingPartnerId}
            onExistingPartnerId={setExistingPartnerId}
          />
        )}
        {step === "partner" && (
          <PartnerStep
            draft={partner}
            onChange={setPartner}
            defaultParentId={scope.id}
            isPlatformAdmin={isPlatformAdmin}
          />
        )}
        {step === "capabilities" && (
          <CapabilitiesStep
            apps={applications}
            features={featureCatalog}
            disabledApps={disabledAppSlugs}
            disabledFeatures={disabledFeaturePaths}
            onToggleApp={(slug, enabled) => {
              setDisabledAppSlugs((prev) => {
                const next = new Set(prev);
                if (enabled) next.delete(slug);
                else next.add(slug);
                return next;
              });
            }}
            onToggleFeature={(path, enabled) => {
              setDisabledFeaturePaths((prev) => {
                const next = new Set(prev);
                if (enabled) next.delete(path);
                else next.add(path);
                return next;
              });
            }}
            loading={applicationsQuery.isLoading || featureCatalogQuery.isLoading}
          />
        )}
        {step === "organization" && (
          <OrganizationStep
            draft={org}
            onChange={setOrg}
            parentPartnerLabel={
              kind === "existing_partner" && existingPartnerId
                ? `Adding to existing partner. The new organization will live under it.`
                : null
            }
          />
        )}
        {step === "location" && (
          <LocationStep
            draft={loc}
            onChange={setLoc}
            verticals={verticals}
            verticalsLoading={verticalsQuery.isLoading}
          />
        )}
        {step === "providers" && (
          <ProvidersStep
            bindings={bindings}
            onChange={setBindings}
            providers={providerCatalog}
            loading={providersQuery.isLoading}
          />
        )}
        {step === "admin" && <AdminStep draft={admin} onChange={setAdmin} />}
        {step === "review" && (
          <ReviewStep
            kind={kind}
            partner={partner}
            organization={org}
            location={loc}
            bindings={bindings}
            admin={admin}
            verticals={verticals}
            providers={providerCatalog}
            applications={applications}
            disabledAppSlugs={disabledAppSlugs}
            disabledFeaturePaths={disabledFeaturePaths}
          />
        )}
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={goBack}
          disabled={stepIndex === 0 || submit.isPending}
          className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-sm disabled:opacity-40"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        {step !== "review" ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canAdvanceFrom(step)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            Next
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={submit.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Rocket className="h-4 w-4" />
            {submit.isPending ? "Creating…" : "Create everything"}
          </button>
        )}
      </div>
    </div>
  );
}

/** Step 1 of #163 - branch the wizard on whether we're standing up
 *  a brand-new partner or adding an org under an existing one.
 *  Skips the "partner" step in the latter mode. The picker is a
 *  thin wrapper around PartnerPicker so callers can swap to a
 *  different partner mid-wizard without losing draft data - only
 *  the partner_id is stored, not the partner row. */
function KindStep(props: {
  kind: OnboardKind;
  onKind: (next: OnboardKind) => void;
  existingPartnerId: string;
  onExistingPartnerId: (next: string) => void;
}): JSX.Element {
  const { kind, onKind, existingPartnerId, onExistingPartnerId } = props;
  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold">What are you onboarding?</h2>
        <p className="text-xs text-muted-foreground">
          Pick the right starting point - the wizard adapts the rest of the steps to match.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        <KindCard
          selected={kind === "new_partner"}
          onSelect={() => onKind("new_partner")}
          title="A new partner"
          subtitle="Stand up a brand-new partner with its first organization, location, providers, and admin user. Use this for a fresh dealership group or merchant brand."
        />
        <KindCard
          selected={kind === "existing_partner"}
          onSelect={() => onKind("existing_partner")}
          title="A new organization under an existing partner"
          subtitle="Add the next organization to a partner you already manage. Skips the partner step; you'll pick the parent partner below."
        />
      </div>

      {kind === "existing_partner" && (
        <div className="space-y-2">
          <div className="block text-xs font-medium text-muted-foreground">Parent partner</div>
          <PartnerPicker
            value={existingPartnerId}
            onChange={onExistingPartnerId}
            emptyLabel="Pick a partner you manage…"
            required
          />
          <p className="text-xs text-muted-foreground">
            The new organization + location will land under this partner. Provider bindings and the
            admin invite still scope to this partner's subtree.
          </p>
        </div>
      )}
    </div>
  );
}

function KindCard(props: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtitle: string;
}): JSX.Element {
  const { selected, onSelect, title, subtitle } = props;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "rounded-lg border p-3 text-left transition",
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "border-border bg-background hover:bg-muted",
      )}
      aria-pressed={selected}
    >
      <div className="text-sm font-medium">{title}</div>
      <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
    </button>
  );
}

function PartnerStep(props: {
  draft: PartnerDraft;
  onChange: (next: PartnerDraft) => void;
  defaultParentId: string;
  isPlatformAdmin: boolean;
}): JSX.Element {
  const { draft, onChange, isPlatformAdmin } = props;
  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold">Step 1 - Partner</h2>
        <p className="text-xs text-muted-foreground">
          Who owns this space? A partner is the top-level brand (or sub-brand) that wraps one or
          more organizations. Pick a parent and give it a display name.
        </p>
      </header>

      <div>
        <label htmlFor="ob-parent" className="mb-1 block text-xs font-medium text-muted-foreground">
          Parent partner
        </label>
        <PartnerPicker
          id="ob-parent"
          value={draft.parent_id}
          onChange={(id) => onChange({ ...draft, parent_id: id })}
          required
          includePlatform
        />
      </div>

      <div>
        <label htmlFor="ob-pname" className="mb-1 block text-xs font-medium text-muted-foreground">
          Partner name
        </label>
        <input
          id="ob-pname"
          type="text"
          required
          value={draft.name}
          onChange={(e) => {
            const name = e.target.value;
            onChange({
              ...draft,
              name,
              slug: draft.slug_touched ? draft.slug : slugify(name),
            });
          }}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Acme Payments"
          autoFocus
        />
      </div>

      <div>
        <label htmlFor="ob-pslug" className="mb-1 block text-xs font-medium text-muted-foreground">
          Slug <span className="text-muted-foreground/80">(a-z, 0-9, hyphens; 3-30 chars)</span>
        </label>
        <input
          id="ob-pslug"
          type="text"
          required
          spellCheck={false}
          value={draft.slug}
          onChange={(e) => onChange({ ...draft, slug: e.target.value, slug_touched: true })}
          pattern="[a-z][a-z0-9\-]{2,29}"
          className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
          placeholder="acme-payments"
        />
      </div>

      <label className="flex items-start gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={draft.can_manage_sub_partners}
          onChange={(e) => onChange({ ...draft, can_manage_sub_partners: e.target.checked })}
          className="mt-0.5 h-3.5 w-3.5 accent-primary"
        />
        <span>
          Let this partner's admins create sub-partners. Useful for white-label resellers; leave off
          for normal customers.
        </span>
      </label>

      {/* V0036 - Platform-only escape hatches. Only super_admin can
          set them on create; backend 403s for everyone else, so the
          checkboxes only render when the caller is super. */}
      {isPlatformAdmin && (
        <>
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={draft.can_cascade_sub_partner_flag}
              onChange={(e) =>
                onChange({ ...draft, can_cascade_sub_partner_flag: e.target.checked })
              }
              className="mt-0.5 h-3.5 w-3.5 accent-primary"
            />
            <span>
              Allow cascading sub-partner creation. When on, a partner_admin on this partner can
              grant <span className="font-mono">can_manage_sub_partners</span>=TRUE on the
              sub-partners they create. Off by default.
            </span>
          </label>
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={draft.can_impersonate}
              onChange={(e) => onChange({ ...draft, can_impersonate: e.target.checked })}
              className="mt-0.5 h-3.5 w-3.5 accent-primary"
            />
            <span>
              Allow partner-admin impersonation of direct-child users. When on, a partner_admin on
              this partner can start impersonation sessions targeting users whose primary partner is
              a DIRECT child of this row.
            </span>
          </label>
        </>
      )}
    </div>
  );
}

/** Apps & features step. Catalogue defaults to everything ON; the
 *  admin's only job here is to opt OUT of anything the new partner
 *  shouldn't get. Apps render as cards with a top-level on/off; each
 *  app card expands to show the feature ltree under that app's
 *  namespace, indented by depth so the hierarchy reads naturally.
 *  Disabling an app collapses + visually disables every feature
 *  underneath, since the app gate dominates the feature gate. */
function CapabilitiesStep(props: {
  apps: Application[];
  features: FeatureCatalogEntry[];
  disabledApps: Set<string>;
  disabledFeatures: Set<string>;
  onToggleApp: (slug: string, enabled: boolean) => void;
  onToggleFeature: (path: string, enabled: boolean) => void;
  loading: boolean;
}): JSX.Element {
  const { apps, features, disabledApps, disabledFeatures, onToggleApp, onToggleFeature, loading } =
    props;

  // Group features by their root segment (the app slug they live
  // under). The root path entry itself - if the catalogue has one -
  // collapses into the app card's header; only deeper paths render
  // as toggleable rows.
  const featuresByApp = useMemo(() => {
    const groups: Record<string, FeatureCatalogEntry[]> = {};
    for (const f of features) {
      if (f.depth < 2) continue; // depth==1 is the root, that IS the app
      const root = f.path.split(".")[0];
      if (!groups[root]) groups[root] = [];
      groups[root].push(f);
    }
    // Lexicographic sort on the path puts children directly under
    // their parent - that's the natural tree order for ltree, e.g.
    // pos.payments → pos.payments.card_present → pos.payments.virtual
    // → pos.reporting. Sorting by depth would group all level-2 rows
    // together before any level-3 row, scrambling the tree.
    for (const k of Object.keys(groups)) {
      groups[k].sort((a, b) => a.path.localeCompare(b.path));
    }
    return groups;
  }, [features]);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleExpand = (slug: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <header>
          <h2 className="text-sm font-semibold">Apps & features</h2>
          <p className="text-xs text-muted-foreground">Loading the platform catalogue…</p>
        </header>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold">Apps & features</h2>
        <p className="text-xs text-muted-foreground">
          Pick which apps and features the new partner gets. Everything is on by default - uncheck
          only the ones you want to keep off. You can change these any time later from the
          Applications tab on the partner.
        </p>
      </header>

      <ul className="divide-y rounded-md border bg-card">
        {apps.map((app) => {
          const appOn = !disabledApps.has(app.slug);
          const isOpen = expanded.has(app.slug);
          const appFeatures = featuresByApp[app.slug] ?? [];
          return (
            <li key={app.slug} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <span className="text-sm font-medium">{app.display_name}</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">
                      {app.slug}
                    </span>
                  </div>
                  {app.description && (
                    <p className="mt-1 text-xs text-muted-foreground">{app.description}</p>
                  )}
                  {appFeatures.length > 0 && (
                    <button
                      type="button"
                      className="mt-1 text-xs text-primary hover:underline disabled:opacity-50"
                      onClick={() => toggleExpand(app.slug)}
                      disabled={!appOn}
                    >
                      {isOpen ? "Hide" : "Show"} {appFeatures.length}{" "}
                      {appFeatures.length === 1 ? "feature" : "features"}
                    </button>
                  )}
                </div>
                <Toggle
                  checked={appOn}
                  onChange={(next) => onToggleApp(app.slug, next)}
                  ariaLabel={`Toggle ${app.display_name}`}
                />
              </div>

              {isOpen && appOn && appFeatures.length > 0 && (
                <ul className="mt-3 space-y-1 border-l-2 border-muted pl-3">
                  {appFeatures.map((f) => {
                    const featOn = !disabledFeatures.has(f.path);
                    // f.depth==2 is a top-level feature under the app
                    // (e.g. pos.payments). depth==3+ are children
                    // (pos.payments.card_present). 24px per nesting
                    // level reads as a clear visual tree.
                    const indent = Math.max(0, f.depth - 2) * 24;
                    return (
                      <li
                        key={f.path}
                        className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-muted/40"
                        style={{ paddingLeft: indent + 8 }}
                      >
                        <div className="min-w-0">
                          <div className="text-sm">{f.display_name}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">
                            {f.path}
                          </div>
                          {f.description && (
                            <p className="text-xs text-muted-foreground">{f.description}</p>
                          )}
                        </div>
                        <Toggle
                          checked={featOn}
                          size="sm"
                          onChange={(next) => onToggleFeature(f.path, next)}
                          ariaLabel={`Toggle ${f.display_name}`}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {(disabledApps.size > 0 || disabledFeatures.size > 0) && (
        <p className="text-xs text-muted-foreground">
          {disabledApps.size > 0 && (
            <span>
              {disabledApps.size} {disabledApps.size === 1 ? "app" : "apps"} off
            </span>
          )}
          {disabledApps.size > 0 && disabledFeatures.size > 0 && <span> · </span>}
          {disabledFeatures.size > 0 && (
            <span>
              {disabledFeatures.size} {disabledFeatures.size === 1 ? "feature" : "features"} off
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function OrganizationStep(props: {
  draft: OrganizationDraft;
  onChange: (next: OrganizationDraft) => void;
  /** Optional banner text shown above the form. Used in
   *  existing_partner mode (#163) to remind the operator that the
   *  org will land under their picked partner - without this hint
   *  the org form looks identical between modes. */
  parentPartnerLabel?: string | null;
}): JSX.Element {
  const { draft, onChange, parentPartnerLabel } = props;
  const emailCheck = validateEmail(draft.contact_email);
  const phoneCheck = validatePhone(draft.contact_phone);
  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold">First organization</h2>
        <p className="text-xs text-muted-foreground">
          Organizations are the customer-facing businesses that sit under the partner - usually the
          merchant's legal entity. You can add more later.
        </p>
        {parentPartnerLabel && (
          <p className="mt-2 rounded-md bg-muted/50 px-2 py-1 text-xs text-muted-foreground">
            {parentPartnerLabel}
          </p>
        )}
      </header>
      <div>
        <label htmlFor="ob-oname" className="mb-1 block text-xs font-medium text-muted-foreground">
          Organization name
        </label>
        <input
          id="ob-oname"
          type="text"
          required
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Acme Coffee Co."
          autoFocus
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label
            htmlFor="ob-ocname"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Contact name <span className="text-muted-foreground/80">(optional)</span>
          </label>
          <input
            id="ob-ocname"
            type="text"
            value={draft.contact_name}
            onChange={(e) => onChange({ ...draft, contact_name: e.target.value })}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label
            htmlFor="ob-ocemail"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Contact email <span className="text-muted-foreground/80">(optional)</span>
          </label>
          <input
            id="ob-ocemail"
            type="email"
            value={draft.contact_email}
            onChange={(e) => onChange({ ...draft, contact_email: e.target.value })}
            aria-invalid={!emailCheck.ok}
            className={cn(
              "w-full rounded-md border bg-background px-3 py-2 text-sm",
              !emailCheck.ok && "border-destructive",
            )}
          />
          {!emailCheck.ok && (
            <p className="mt-1 text-[11px] text-destructive">{emailCheck.error}</p>
          )}
        </div>
      </div>
      <div>
        <label htmlFor="ob-ophone" className="mb-1 block text-xs font-medium text-muted-foreground">
          Contact phone <span className="text-muted-foreground/80">(optional)</span>
        </label>
        <input
          id="ob-ophone"
          type="tel"
          value={draft.contact_phone}
          onChange={(e) => onChange({ ...draft, contact_phone: e.target.value })}
          aria-invalid={!phoneCheck.ok}
          className={cn(
            "w-full rounded-md border bg-background px-3 py-2 text-sm",
            !phoneCheck.ok && "border-destructive",
          )}
        />
        {!phoneCheck.ok && <p className="mt-1 text-[11px] text-destructive">{phoneCheck.error}</p>}
      </div>
      <div>
        <label
          htmlFor="ob-oaddress"
          className="mb-1 block text-xs font-medium text-muted-foreground"
        >
          Business address <span className="text-muted-foreground/80">(optional)</span>
        </label>
        <textarea
          id="ob-oaddress"
          value={draft.address}
          onChange={(e) => onChange({ ...draft, address: e.target.value })}
          rows={2}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        />
      </div>
    </div>
  );
}

function LocationStep(props: {
  draft: LocationDraft;
  onChange: (next: LocationDraft) => void;
  verticals: Vertical[];
  verticalsLoading: boolean;
}): JSX.Element {
  const { draft, onChange, verticals } = props;
  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold">Step 3 - First location</h2>
        <p className="text-xs text-muted-foreground">
          Where will the first POS run? Pick a vertical to auto-seed a sensible set of departments -
          most merchants don't need to touch this later.
        </p>
      </header>
      <div>
        <label htmlFor="ob-lname" className="mb-1 block text-xs font-medium text-muted-foreground">
          Location name
        </label>
        <input
          id="ob-lname"
          type="text"
          required
          value={draft.name}
          onChange={(e) => {
            const name = e.target.value;
            onChange({
              ...draft,
              name,
              slug: draft.slug_touched ? draft.slug : slugify(name),
            });
          }}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Downtown"
          autoFocus
        />
      </div>
      <div>
        <label htmlFor="ob-lslug" className="mb-1 block text-xs font-medium text-muted-foreground">
          Slug
        </label>
        <input
          id="ob-lslug"
          type="text"
          required
          spellCheck={false}
          value={draft.slug}
          onChange={(e) => onChange({ ...draft, slug: e.target.value, slug_touched: true })}
          pattern="[a-z][a-z0-9\-]{2,29}"
          className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
          placeholder="acme-downtown"
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label
            htmlFor="ob-lvert"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Vertical
          </label>
          <select
            id="ob-lvert"
            value={draft.vertical_id}
            onChange={(e) => onChange({ ...draft, vertical_id: e.target.value })}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {props.verticalsLoading && <option value="">Loading…</option>}
            {verticals.map((v) => (
              <option key={v.id} value={v.id}>
                {v.display_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ob-ltz" className="mb-1 block text-xs font-medium text-muted-foreground">
            Timezone
          </label>
          <select
            id="ob-ltz"
            value={draft.timezone}
            onChange={(e) => onChange({ ...draft, timezone: e.target.value })}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {timezoneChoicesWithBrowser().map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <label className="flex items-start gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={draft.seed_departments}
          onChange={(e) => onChange({ ...draft, seed_departments: e.target.checked })}
          className="mt-0.5 h-3.5 w-3.5 accent-primary"
        />
        <span>
          Seed default departments from this vertical. Recommended - saves ~5 minutes of manual
          setup per location.
        </span>
      </label>
      <div>
        <label htmlFor="ob-ltax" className="mb-1 block text-xs font-medium text-muted-foreground">
          Sales tax <span className="text-muted-foreground/80">(percent; optional)</span>
        </label>
        <input
          id="ob-ltax"
          type="number"
          inputMode="decimal"
          min="0"
          max="99.99"
          step="0.01"
          value={draft.sales_tax_rate}
          onChange={(e) => onChange({ ...draft, sales_tax_rate: e.target.value })}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="8.75"
        />
      </div>
    </div>
  );
}

function ProvidersStep(props: {
  bindings: ProviderBindingDraft[];
  onChange: (next: ProviderBindingDraft[]) => void;
  providers: ProviderCatalogEntry[];
  loading: boolean;
}): JSX.Element {
  const { bindings, onChange, providers, loading } = props;
  const grouped = useMemo(() => groupProvidersByCategory(providers), [providers]);

  /**
   * Find the first ``(provider, feature_path)`` combo that isn't
   * already represented in ``bindings``. Used by addBinding to default
   * new rows to something useful, and by BindingCard to drive
   * provider/feature change cascades. Walks the catalog in display
   * order so the dropdown's first option matches the auto-pick.
   * Returns ``null`` if every combo is taken.
   */
  const firstAvailableCombo = (
    excludeBindingId: string | null,
    onlyProviderId?: string,
  ): { provider_id: string; feature_path: string } | null => {
    const isTaken = (provId: string, fp: string): boolean =>
      bindings.some(
        (b) => b.id !== excludeBindingId && b.provider_id === provId && b.feature_path === fp,
      );
    for (const prov of providers) {
      if (onlyProviderId && prov.id !== onlyProviderId) continue;
      for (const fp of prov.supported_feature_paths) {
        if (!isTaken(prov.id, fp)) {
          return { provider_id: prov.id, feature_path: fp };
        }
      }
    }
    return null;
  };

  /**
   * Look back through earlier bindings for one bound to the same
   * provider; copy its credential block (label, env, values) AND
   * config_values so the admin doesn't retype the same Valor merchant
   * id / API key - or the partner-scope config like batch settlement
   * time - on every subtype. Empty object when no prior match.
   *
   * config_values copies wholesale even though SchemaForm renders a
   * scope-filtered subset: the unrendered keys are still relevant
   * because the submit pass scope-filters again before POSTing, and
   * carrying everything keeps the data coherent if the admin flips
   * the binding's scope after the prefill.
   */
  const credentialPrefillFor = (
    excludeBindingId: string | null,
    providerId: string,
  ): Partial<ProviderBindingDraft> => {
    const prior = bindings.find((b) => b.id !== excludeBindingId && b.provider_id === providerId);
    if (!prior) return {};
    return {
      environment: prior.environment,
      credential_label: prior.credential_label,
      credential_values: { ...prior.credential_values },
      skip_credential: prior.skip_credential,
      config_values: { ...prior.config_values },
    };
  };

  const addBinding = (): void => {
    const combo = firstAvailableCombo(null);
    if (!combo) return; // every combo is taken - button is also disabled below.
    const prefill = credentialPrefillFor(null, combo.provider_id);
    onChange([
      ...bindings,
      {
        id: crypto.randomUUID(),
        provider_id: combo.provider_id,
        feature_path: combo.feature_path,
        scope: "partner",
        environment: "sandbox",
        credential_label: "",
        credential_values: {},
        skip_credential: false,
        config_values: {},
        ...prefill,
      },
    ]);
  };

  const patch = (id: string, updates: Partial<ProviderBindingDraft>): void => {
    onChange(bindings.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };
  const remove = (id: string): void => onChange(bindings.filter((b) => b.id !== id));

  // True when no fresh combo remains across the whole catalog. Drives
  // the "+ Add provider binding" button's disabled state so the admin
  // can't add a row that has nothing to bind.
  const noFreeCombos = providers.length === 0 || firstAvailableCombo(null) === null;

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold">Step 4 - Providers (optional)</h2>
        <p className="text-xs text-muted-foreground">
          Pre-wire providers with credentials + partner-scope defaults so every new org/location
          inherits them. Skip this step and add bindings from the Providers tab later if you don't
          have credentials yet.
        </p>
      </header>

      {loading && <p className="text-sm text-muted-foreground">Loading catalog…</p>}

      {!loading && bindings.length === 0 && (
        <div className="rounded-md border border-dashed bg-muted/30 p-4 text-center text-sm text-muted-foreground">
          No provider bindings queued. Skip this step if you're not sure - add them later once
          you've got credentials.
        </div>
      )}

      <ul className="space-y-3">
        {bindings.map((b) => (
          <li key={b.id}>
            <BindingCard
              binding={b}
              allBindings={bindings}
              grouped={grouped}
              providers={providers}
              firstAvailableCombo={firstAvailableCombo}
              credentialPrefillFor={credentialPrefillFor}
              onPatch={(updates) => patch(b.id, updates)}
              onRemove={() => remove(b.id)}
            />
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={addBinding}
        disabled={noFreeCombos}
        title={noFreeCombos ? "Every (provider, feature) combo is already queued" : undefined}
        className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
      >
        + Add provider binding
      </button>
    </div>
  );
}

function BindingCard(props: {
  binding: ProviderBindingDraft;
  allBindings: ProviderBindingDraft[];
  grouped: Array<{ category: IntegrationCategory; providers: ProviderCatalogEntry[] }>;
  providers: ProviderCatalogEntry[];
  firstAvailableCombo: (
    excludeBindingId: string | null,
    onlyProviderId?: string,
  ) => { provider_id: string; feature_path: string } | null;
  credentialPrefillFor: (
    excludeBindingId: string | null,
    providerId: string,
  ) => Partial<ProviderBindingDraft>;
  onPatch: (updates: Partial<ProviderBindingDraft>) => void;
  onRemove: () => void;
}): JSX.Element {
  const {
    binding: b,
    allBindings,
    grouped,
    providers,
    firstAvailableCombo,
    credentialPrefillFor,
    onPatch,
    onRemove,
  } = props;
  const provider = providers.find((p) => p.id === b.provider_id);
  const featurePathOptions = provider?.supported_feature_paths ?? [];
  const hasCredSchema = hasProperties(provider?.credential_schema);
  const hasConfigSchema = hasProperties(provider?.config_schema);
  const isNonPayment = provider?.category && provider.category !== "payment";

  // True when (provId, feature_path) is already used by some OTHER
  // binding draft. Drives the feature dropdown's filtered options.
  const isComboTaken = (provId: string, fp: string): boolean =>
    allBindings.some(
      (other) => other.id !== b.id && other.provider_id === provId && other.feature_path === fp,
    );

  // Provider list filtered to those with at least one combo still
  // free. Always include the currently-selected provider.
  const providersWithFreeCombo = providers.filter(
    (p) => p.id === b.provider_id || firstAvailableCombo(b.id, p.id) !== null,
  );
  const groupedFiltered = grouped
    .map((group) => ({
      category: group.category,
      providers: group.providers.filter((p) => providersWithFreeCombo.includes(p)),
    }))
    .filter((g) => g.providers.length > 0);

  // Feature dropdown: drop feature_paths already taken by another
  // binding for this provider. Currently-selected feature stays
  // visible.
  const eligibleFeaturePaths = featurePathOptions.filter(
    (fp) => fp === b.feature_path || !isComboTaken(b.provider_id, fp),
  );

  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs">
          <span className="mb-1 block font-medium text-muted-foreground">Provider</span>
          <select
            value={b.provider_id}
            onChange={(e) => {
              const nextProviderId = e.target.value;
              const combo = firstAvailableCombo(b.id, nextProviderId);
              const prefill = credentialPrefillFor(b.id, nextProviderId);
              onPatch({
                provider_id: nextProviderId,
                feature_path: combo?.feature_path ?? "",
                config_values: {},
                credential_values: {},
                ...prefill,
              });
            }}
            className="w-full rounded-md border bg-background px-2 py-1.5"
          >
            {groupedFiltered.map(({ category, providers: catProviders }) => (
              <optgroup key={category} label={CATEGORY_LABEL[category]}>
                {catProviders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-1 block font-medium text-muted-foreground">Feature</span>
          <select
            value={b.feature_path}
            onChange={(e) => onPatch({ feature_path: e.target.value })}
            disabled={eligibleFeaturePaths.length === 0}
            className="w-full rounded-md border bg-background px-2 py-1.5 disabled:opacity-50"
          >
            {eligibleFeaturePaths.length === 0 && (
              <option value="">No features supported for this provider</option>
            )}
            {eligibleFeaturePaths.map((fp) => {
              const labels = fp.split(".");
              const leaf = labels[labels.length - 1] ?? fp;
              const display = leaf.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
              return (
                <option key={fp} value={fp}>
                  {display} · {fp}
                </option>
              );
            })}
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-1 block font-medium text-muted-foreground">Scope</span>
          <select
            value={b.scope}
            onChange={(e) =>
              // Clear config_values on scope change - SchemaForm hides
              // out-of-scope fields but keeps their prior values in
              // the bag, so without the reset a partner-filled value
              // could leak into a subsequent location-scoped write.
              onPatch({
                scope: e.target.value as BindingScope,
                config_values: {},
              })
            }
            className="w-full rounded-md border bg-background px-2 py-1.5"
          >
            <option value="partner">Partner (cascades to all)</option>
            <option value="organization">Organization (this org)</option>
            <option value="location">Location (this location)</option>
          </select>
        </label>
      </div>

      {isNonPayment && (
        <div className="mt-3 rounded-md border border-dashed bg-muted/30 p-2 text-[11px] text-muted-foreground">
          {provider?.display_name} is a {CATEGORY_LABEL[provider!.category!].toLowerCase()} provider
          - we'll create the credential now, but the binding has to be wired from the Providers tab
          once you're done (quick onboard only creates payment bindings).
        </div>
      )}

      {/* Credential section - rendered whenever the provider declares
          credential fields. Skip checkbox lets the admin defer
          credentials to a later "Rotate" from the Providers tab. */}
      {hasCredSchema && (
        <div className="mt-3 space-y-2 rounded-md border bg-muted/20 p-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Credential
            </h4>
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={b.skip_credential}
                onChange={(e) => onPatch({ skip_credential: e.target.checked })}
                className="h-3 w-3 accent-primary"
              />
              Skip - I'll add credentials later
            </label>
          </div>

          {!b.skip_credential && (
            <>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="text-xs">
                  <span className="mb-1 block font-medium text-muted-foreground">Environment</span>
                  <select
                    value={b.environment}
                    onChange={(e) =>
                      onPatch({ environment: e.target.value as ProviderEnvironment })
                    }
                    className="w-full rounded-md border bg-background px-2 py-1.5"
                  >
                    <option value="sandbox">Sandbox</option>
                    <option value="production">Production</option>
                  </select>
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-medium text-muted-foreground">
                    Label <span className="text-muted-foreground/80">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={b.credential_label}
                    onChange={(e) => onPatch({ credential_label: e.target.value })}
                    placeholder={defaultCredentialLabel(provider, b.feature_path, b.environment)}
                    className="w-full rounded-md border bg-background px-2 py-1.5"
                  />
                </label>
              </div>
              {provider && (
                <SchemaForm
                  schema={provider.credential_schema}
                  values={b.credential_values}
                  onChange={(next) => onPatch({ credential_values: next })}
                  caption="Credential fields are encrypted at rest."
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Config section - scope-aware: only shows the fields whose
          ``x-scope`` matches the chosen binding scope. Partner-scope
          bindings render partner-tier defaults (URLs, batch-cutover)
          so they cascade to every location. */}
      {hasConfigSchema && provider && (
        <div className="mt-3 space-y-2 rounded-md border bg-muted/20 p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Binding config ({b.scope})
          </h4>
          <SchemaForm
            schema={provider.config_schema}
            values={b.config_values}
            onChange={(next) => onPatch({ config_values: next })}
            scopeFilter={b.scope}
            ambient={{ feature_path: b.feature_path }}
            caption="Catalog defaults apply for fields you leave blank; descendant scopes can override."
          />
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{provider?.description ?? ""}</span>
        <button
          type="button"
          onClick={onRemove}
          className="rounded border border-destructive/40 bg-destructive/5 px-2 py-0.5 text-destructive hover:bg-destructive/10"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function AdminStep(props: {
  draft: AdminDraft;
  onChange: (next: AdminDraft) => void;
}): JSX.Element {
  const { draft, onChange } = props;
  const emailCheck = validateEmail(draft.email, { required: draft.invite });
  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold">Step 5 - First admin user</h2>
        <p className="text-xs text-muted-foreground">
          Invite the person who'll run this partner day-to-day. They'll get a Cognito welcome email
          with a password-setup link. You can also skip and invite from the Users page later.
        </p>
      </header>

      <label className="flex items-start gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={draft.invite}
          onChange={(e) => onChange({ ...draft, invite: e.target.checked })}
          className="mt-0.5 h-3.5 w-3.5 accent-primary"
        />
        <span>
          Send an invite email right after creation. Uncheck to skip - no admin user will be created
          and you'll need to add one from the Users page before anyone can log in.
        </span>
      </label>

      {draft.invite && (
        <>
          <div>
            <label
              htmlFor="ob-aemail"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Email
            </label>
            <input
              id="ob-aemail"
              type="email"
              required
              value={draft.email}
              onChange={(e) => onChange({ ...draft, email: e.target.value })}
              aria-invalid={!emailCheck.ok}
              placeholder="admin@acme.com"
              className={cn(
                "w-full rounded-md border bg-background px-3 py-2 text-sm",
                !emailCheck.ok && "border-destructive",
              )}
              autoFocus
            />
            {!emailCheck.ok && (
              <p className="mt-1 text-[11px] text-destructive">{emailCheck.error}</p>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label
                htmlFor="ob-afname"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                First name <span className="text-muted-foreground/80">(optional)</span>
              </label>
              <input
                id="ob-afname"
                type="text"
                value={draft.first_name}
                onChange={(e) => onChange({ ...draft, first_name: e.target.value })}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="ob-alname"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Last name <span className="text-muted-foreground/80">(optional)</span>
              </label>
              <input
                id="ob-alname"
                type="text"
                value={draft.last_name}
                onChange={(e) => onChange({ ...draft, last_name: e.target.value })}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="ob-arole"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Role
            </label>
            <select
              id="ob-arole"
              value={draft.role}
              onChange={(e) => onChange({ ...draft, role: e.target.value as UserRole })}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="partner_admin">Partner admin (recommended)</option>
              <option value="partner_viewer">Partner viewer (read-only)</option>
            </select>
          </div>
        </>
      )}
    </div>
  );
}

function ReviewStep(props: {
  kind: OnboardKind;
  partner: PartnerDraft;
  organization: OrganizationDraft;
  location: LocationDraft;
  bindings: ProviderBindingDraft[];
  admin: AdminDraft;
  verticals: Vertical[];
  providers: ProviderCatalogEntry[];
  applications: Application[];
  disabledAppSlugs: Set<string>;
  disabledFeaturePaths: Set<string>;
}): JSX.Element {
  const {
    kind,
    partner,
    organization,
    location,
    bindings,
    admin,
    verticals,
    providers,
    applications,
    disabledAppSlugs,
    disabledFeaturePaths,
  } = props;
  const vertical = verticals.find((v) => v.id === location.vertical_id);
  const showCapabilities = kind === "new_partner";
  const disabledAppNames = applications
    .filter((a) => disabledAppSlugs.has(a.slug))
    .map((a) => a.display_name);
  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold">Step 6 - Review</h2>
        <p className="text-xs text-muted-foreground">
          Everything below is created in one click, in this exact order. If a step fails we stop
          there - finish manually from the detail pages.
        </p>
      </header>

      <Section title="Partner" icon={Building2}>
        <Row label="Name" value={partner.name} />
        <Row label="Slug" value={partner.slug} mono />
        <Row
          label="Sub-partner self-serve"
          value={partner.can_manage_sub_partners ? "Enabled" : "Off"}
        />
      </Section>

      <Section title="Organization" icon={Store}>
        <Row label="Name" value={organization.name} />
        {organization.contact_name && <Row label="Contact" value={organization.contact_name} />}
        {organization.contact_email && <Row label="Email" value={organization.contact_email} />}
        {organization.contact_phone && <Row label="Phone" value={organization.contact_phone} />}
      </Section>

      <Section title="Location" icon={MapPin}>
        <Row label="Name" value={location.name} />
        <Row label="Slug" value={location.slug} mono />
        <Row label="Vertical" value={vertical?.display_name ?? "-"} />
        <Row label="Timezone" value={location.timezone} mono />
        <Row
          label="Seed departments"
          value={location.seed_departments ? "Yes (from vertical)" : "No"}
        />
      </Section>

      {showCapabilities && (
        <Section title="Apps & features" icon={Layers}>
          {disabledAppSlugs.size === 0 && disabledFeaturePaths.size === 0 ? (
            <p className="text-xs text-muted-foreground">
              All apps and features stay enabled (catalogue defaults).
            </p>
          ) : (
            <>
              {disabledAppNames.length > 0 && (
                <Row label="Apps off" value={disabledAppNames.join(", ")} />
              )}
              {disabledFeaturePaths.size > 0 && (
                <Row
                  label="Features off"
                  value={`${disabledFeaturePaths.size} ${
                    disabledFeaturePaths.size === 1 ? "path" : "paths"
                  }`}
                />
              )}
            </>
          )}
        </Section>
      )}

      <Section title={`Providers (${bindings.length})`} icon={Plug}>
        {bindings.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            None - wire them up from the Providers tab.
          </p>
        ) : (
          <ul className="space-y-1 text-xs">
            {bindings.map((b) => {
              const p = providers.find((pp) => pp.id === b.provider_id);
              const credPart = b.skip_credential ? "no credential" : `credential ${b.environment}`;
              return (
                <li key={b.id} className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  <span>
                    <strong>{p?.display_name ?? "-"}</strong>{" "}
                    <span className="font-mono text-muted-foreground">{b.feature_path}</span> -{" "}
                    {b.scope} · {credPart}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="Admin user" icon={UserPlus}>
        {!admin.invite ? (
          <p className="text-xs text-muted-foreground">
            Skipped - no user will be invited. Add one from the Users page before anyone can log in.
          </p>
        ) : (
          <>
            <Row label="Email" value={admin.email} />
            {admin.first_name && <Row label="First name" value={admin.first_name} />}
            {admin.last_name && <Row label="Last name" value={admin.last_name} />}
            <Row label="Role" value={admin.role} />
          </>
        )}
      </Section>
    </div>
  );
}

function Section(props: {
  title: string;
  icon: typeof Building2;
  children: React.ReactNode;
}): JSX.Element {
  const Icon = props.icon;
  return (
    <section className="rounded-lg border bg-muted/20 p-3">
      <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {props.title}
      </h3>
      <div className="space-y-1">{props.children}</div>
    </section>
  );
}

function Row(props: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{props.label}</span>
      <span className={cn("truncate text-right", props.mono && "font-mono")}>{props.value}</span>
    </div>
  );
}
